import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faEnvelope,
  faPhone,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { SITE_ORIGIN } from "../../config/appConfig";
import { FAVICON_URL, cdnAssetUrl } from "../../config/staticAssets";
import { USER_AGREEMENT_SECTIONS } from "./userAgreementContent";
import "./TermsAndCondition.css";
import "./UserAgreement.css";

const PAGE_META = {
  title: "User Agreement | Welinkjobs",
  description:
    "Users can search for and apply to job opportunities globally through an interactive platform.",
  keywords: "HTML, CSS, JavaScript",
  author: "WeLinkJobs LLC",
  canonicalPath: "/user-agreement",
  siteName: "Welinkjobs",
  themeColor: "#012660",
};

const NAV_LINKS = [
  { label: "About", href: "/about" },
  { label: "Blogs", href: "https://welinkjobs.com/blogs", external: true },
  { label: "Contact", href: "/about#contactform" },
  { label: "FAQs", href: "/faq" },
  { label: "Download App", href: "/downlaodapp" },
];

const MARKETING_STYLESHEETS = [
  "assets/css/bootstrap.min.css",
  "assets/css/fontawesome.css",
  "assets/css/animate.css",
  "assets/css/swiper.min.css",
  "assets/css/odometer.css",
  "assets/css/nice-select.css",
  "assets/css/aos.css",
  "assets/css/jquery-ui.min.css",
  "assets/css/magnific-popup.css",
  "assets/css/rs6.css",
  "assets/css/main.css",
];

function renderBlock(block, index) {
  if (block.type === "h3") {
    return (
      <h3 key={index} className="wlj-subsection-title">
        {block.text}
      </h3>
    );
  }
  if (block.type === "h4") {
    return (
      <h4 key={index} className="wlj-subsection-title wlj-subsection-title--sm">
        {block.text}
      </h4>
    );
  }
  if (block.type === "ul") {
    return (
      <ul key={index} className="wlj-terms-list">
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }
  return (
    <p key={index}>
      {block.text}
    </p>
  );
}

function UserAgreement() {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [headerFixed, setHeaderFixed] = useState(false);

  const canonicalUrl = `${SITE_ORIGIN}${PAGE_META.canonicalPath}`;
  const pageUrl = useMemo(() => {
    if (typeof window === "undefined") return canonicalUrl;
    return `${window.location.origin}${pathname}`;
  }, [canonicalUrl, pathname]);

  useEffect(() => {
    const onScroll = () => {
      setShowTop(window.scrollY > 280);
      setHeaderFixed(window.scrollY > 100);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const html = document.documentElement;
    const prevTheme = html.getAttribute("data-theme");
    const prevBsTheme = html.getAttribute("data-bs-theme");
    const hadDarkMode = document.body.classList.contains("darkMode");
    const hadLightMode = document.body.classList.contains("lightMode");

    html.setAttribute("data-theme", "light");
    html.setAttribute("data-bs-theme", "light");
    document.body.classList.add("wlj-terms-page-active");
    document.body.classList.remove("darkMode");
    document.body.classList.add("lightMode");

    return () => {
      document.body.classList.remove("wlj-terms-page-active");
      if (prevTheme) html.setAttribute("data-theme", prevTheme);
      else html.removeAttribute("data-theme");
      if (prevBsTheme) html.setAttribute("data-bs-theme", prevBsTheme);
      else html.removeAttribute("data-bs-theme");
      document.body.classList.toggle("darkMode", hadDarkMode);
      document.body.classList.toggle("lightMode", hadLightMode);
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const renderNavLink = (item, onClick) => {
    const content = <span>{item.label}</span>;
    if (item.external) {
      return (
        <a href={item.href} target="_blank" rel="noreferrer" onClick={onClick}>
          {content}
        </a>
      );
    }
    if (item.href.startsWith("/")) {
      return (
        <Link to={item.href} onClick={onClick}>
          {content}
        </Link>
      );
    }
    return (
      <a href={item.href} onClick={onClick}>
        {content}
      </a>
    );
  };

  return (
    <>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <title>{PAGE_META.title}</title>
        <meta name="description" content={PAGE_META.description} />
        <meta name="keywords" content={PAGE_META.keywords} />
        <meta name="author" content={PAGE_META.author} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="theme-color" content={PAGE_META.themeColor} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="image_src" href={FAVICON_URL} />
        <link rel="shortcut icon" href={FAVICON_URL} type="image/x-icon" />
        <link rel="icon" href={FAVICON_URL} />
        {MARKETING_STYLESHEETS.map((path) => (
          <link
            key={path}
            rel="stylesheet"
            href={cdnAssetUrl(path)}
            data-marketing-style={path}
          />
        ))}
        <meta property="og:title" content={PAGE_META.title} />
        <meta property="og:description" content={PAGE_META.description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={PAGE_META.siteName} />
        <meta property="og:image" content={FAVICON_URL} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_META.title} />
        <meta name="twitter:description" content={PAGE_META.description} />
      </Helmet>

      <div className={`wlj-terms-backtotop ${showTop ? "is-visible" : ""}`}>
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      </div>

      <div className={`wlj-terms-page wlj-user-agreement-page ${headerFixed ? "wlj-header-fixed-page" : ""}`}>
        <header className={`wlj-terms-header ${headerFixed ? "is-fixed" : ""}`}>
          <div className="wlj-header-inner">
            <div className="wlj-container wlj-mxw-1710">
              <div className="wlj-ul-between wlj-header-bar">
                <div className="wlj-header-logo">
                  <Link to="/about">
                    <img src={cdnAssetUrl("assets/welink.79ac1260.svg")} alt="Logo" />
                  </Link>
                </div>

                <div className="wlj-header-nav-wrap">
                  <nav className="wlj-header-nav">
                    <ul>
                      {NAV_LINKS.map((item) => (
                        <li key={item.label} className={item.active ? "active" : ""}>
                          {renderNavLink(item)}
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>

                <div className="wlj-header-actions">
                  <a className="wlj-btn" href="https://welinkjobs.com/NewsFeed" target="_blank" rel="noreferrer">
                    EXPLORE
                  </a>
                  <a className="wlj-btn" href="https://welinkjobs.com/signIn" target="_blank" rel="noreferrer">
                    LOGIN / SIGNUP
                  </a>
                </div>

                <button type="button" className="wlj-hamburger" onClick={() => setMenuOpen(true)} aria-label="Open menu">
                  <span className="wlj-hamburger-icon" />
                </button>
              </div>
            </div>
          </div>

          <div className={`wlj-mobile-menu ${menuOpen ? "is-open" : ""}`}>
            <div className="wlj-mobile-menu-scroll">
              <button type="button" className="wlj-mobile-close" onClick={closeMenu}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <div className="wlj-mobile-logo">
                <Link to="/about" onClick={closeMenu}>
                  <img src={cdnAssetUrl("assets/welink.79ac1260.svg")} alt="Logo" />
                </Link>
              </div>
              <ul className="wlj-mobile-nav">
                {NAV_LINKS.map((item) => (
                  <li key={item.label} className={item.active ? "active" : ""}>
                    {renderNavLink(item, closeMenu)}
                  </li>
                ))}
                <li>
                  <a href="https://welinkjobs.com/NewsFeed" target="_blank" rel="noreferrer" onClick={closeMenu}>
                    Explore
                  </a>
                </li>
                <li>
                  <a href="https://welinkjobs.com/signIn" target="_blank" rel="noreferrer" onClick={closeMenu}>
                    LOGIN / SIGNUP
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div
            className={`wlj-mobile-backdrop ${menuOpen ? "is-open" : ""}`}
            onClick={closeMenu}
            aria-hidden="true"
          />
        </header>

        <div className="wlj-terms-body-overlay" />

        <main className="wlj-terms-main">
          <section className="wlj-feature">
            <div className="wlj-container wlj-pt-50">
              <div className="wlj-row">
                <div className="wlj-col-md-12">
                  <div className="wlj-breadcrumb-content">
                    <h2 className="wlj-page-title">User Agreement</h2>
                  </div>
                </div>
              </div>
            </div>

            <div className="wlj-container">
              <div className="wlj-content-wrap">
                <div className="wlj-col-xl-12 wlj-col-lg-8">
                  {USER_AGREEMENT_SECTIONS.map((section) => (
                    <div key={section.title} className="wlj-mb-30">
                      <h2 className="wlj-section-title">{section.title}</h2>
                      <div className="wlj-feature-item wlj-mt-30">
                        {section.blocks.map((block, index) => renderBlock(block, index))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="wlj-terms-footer wlj-pt-80 wlj-pb-25">
          <div className="wlj-container">
            <div
              className="wlj-footer-cta"
              style={{
                "--wlj-cta-shadow": `url(${cdnAssetUrl("assets/img/shape/cta_shadow.png")})`,
              }}
            >
              <div className="wlj-footer-cta-item wlj-mt-30">
                <a href="mailto:info@welinkjobs.com" className="wlj-footer-icon wlj-mr-35" aria-label="Email">
                  <FontAwesomeIcon icon={faEnvelope} />
                </a>
                <div>
                  <span className="wlj-footer-cta-label">Drop here a note</span>
                  <h3 className="wlj-footer-cta-value">info@welinkjobs.com</h3>
                </div>
              </div>

              <div className="wlj-footer-cta-item wlj-mt-30">
                <a href="tel:+13072436345" className="wlj-footer-icon wlj-mr-35" aria-label="Phone">
                  <FontAwesomeIcon icon={faPhone} />
                </a>
                <div>
                  <span className="wlj-footer-cta-label">Call us Directly</span>
                  <h3 className="wlj-footer-cta-value">+1 3072436345</h3>
                </div>
              </div>

              <span className="wlj-footer-cta-line" aria-hidden="true" />
            </div>

            <div className="wlj-footer-links wlj-mt-12">
              <div className="wlj-mt-none-40 wlj-flex-between">
                <div className="wlj-footer-col wlj-mt-40">
                  <ul>
                    <li>
                      <Link to="/about">About Us</Link>
                    </li>
                    <li>
                      <Link to="/about#contactform">Contact Us</Link>
                    </li>
                  </ul>
                </div>
                <div className="wlj-footer-col wlj-mt-40">
                  <ul>
                    <li>
                      <Link to="/privacy-policy">Privacy Policy</Link>
                    </li>
                    <li>
                      <Link to="/faq">FAQs</Link>
                    </li>
                  </ul>
                </div>
                <div className="wlj-footer-col wlj-mt-40">
                  <ul>
                    <li>
                      <Link to="/terms-and-conditions">Terms and Conditions</Link>
                    </li>
                    <li>
                      <Link to="/user-agreement">User Agreement</Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="wlj-footer-copy wlj-mt-12">
              <p className="wlj-mt-15">
                Copyright © <a href="https://welinkjobs.com/">Welinkjobs LLC</a>. All rights reserved.
              </p>
            </div>
          </div>

          <div className="wlj-footer-shapes" aria-hidden="true">
            <div className="wlj-shape wlj-shape--1">
              <img src={cdnAssetUrl("assets/img/shape/ft_shape1.png")} alt="" />
            </div>
            <div className="wlj-shape wlj-shape--2">
              <img src={cdnAssetUrl("assets/img/shape/ft_shape2.png")} alt="" />
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default UserAgreement;
